<template>
  <div class="substep-set-up-project">
    <div class="substep-layout">
      <div class="substep-form">
        <div class="substep-header">
          <h3 data-testid="step-title">Customize your first project</h3>
          <p>Watch the preview update as you go. This is what your team — and your client — will see.</p>
        </div>

        <div class="form-fields">
          <div :class="['field-group', { 'has-error': errors.projectName }]">
            <label
              class="field-label"
              for="project-name"
            >PROJECT NAME <span class="required-mark">*</span></label>
            <AppInput
              id="project-name"
              v-model="projectName"
              placeholder="e.g. Acme — website refresh"
              data-testid="project-name-input"
              maxlength="256"
              autofocus
            />
            <p
              v-if="errors.projectName"
              class="error help-block"
            >{{ errors.projectName }}</p>
          </div>

          <div class="field-group">
            <label
              class="field-label"
              for="client-select"
            >CLIENT</label>
            <AppSelect
              v-model="selectedClientId"
              :options="clientOptions"
              :select2-options="{ width: '100%' }"
              data-testid="client-select"
            />
            <p class="field-hint">Optional — used for reporting and invoicing.</p>
            <AppInput
              v-if="selectedClientId === 'new'"
              v-model="newClientName"
              placeholder="Client name"
              class="new-client-input"
              data-testid="new-client-name-input"
              maxlength="100"
            />
            <p
              v-if="selectedClientId === 'new'"
              class="field-hint"
              data-testid="new-client-name-hint"
            >Maximum 100 characters.</p>
          </div>

          <div
            v-if="!hideBudgetCard"
            class="budget-card"
          >
            <div class="card-row">
              <div class="card-copy">
                <div class="card-title">
                  <i class="hi hi-epic budget-icon"></i>
                  <span>Weekly project budget</span>
                  <span class="badge badge-filled">Recommended</span>
                </div>
                <p class="card-description">We suggest 40 total hours per week. You can increase this if your team is larger.</p>
              </div>
              <AppSwitch
                v-model="budgetEnabled"
                data-testid="budget-toggle"
              />
            </div>

            <div
              v-if="budgetEnabled"
              :class="['budget-inputs', { 'has-error': errors.budgetAmount }]"
              data-testid="budget-inputs"
            >
              <div class="budget-input-row">
                <AppInput
                  v-model="budgetAmount"
                  placeholder="40"
                  data-testid="budget-amount-input"
                  inputmode="numeric"
                  maxlength="3"
                />
                <span class="budget-input-suffix">/ week</span>
              </div>
              <p
                v-if="errors.budgetAmount"
                class="error help-block"
              >{{ errors.budgetAmount }}</p>
            </div>
          </div>

          <div class="client-viewer-card client-viewer-card--disabled">
            <div class="card-row">
              <div class="card-copy">
                <div class="card-title">
                  <i class="hi hi-eye hi-16"></i>
                  <span class="card-title-text--muted">Client viewer</span>
                  <span class="badge badge-dark">Free</span>
                </div>
                <p class="card-description card-description--muted">
                  You can later add your client as a viewer to see live hours on this project for free — no seat cost.
                </p>
              </div>
              <AppSwitch
                :value="false"
                disabled
              />
            </div>
          </div>
        </div>
      </div>

      <div class="substep-preview">
        <h4 class="preview-label">
          <i class="hi hi-eye hi-16"></i>
          Preview · What your team sees
        </h4>
        <div class="tracker-preview">
          <div class="tracker-chrome">
            <div class="chrome-dots">
              <span class="chrome-dot chrome-dot--red" />
              <span class="chrome-dot chrome-dot--yellow" />
              <span class="chrome-dot chrome-dot--green" />
            </div>
            <span class="chrome-menu">···</span>
          </div>
          <div class="tracker-body">
            <div class="timer-display">01:27:55</div>
            <div
              class="tracker-project-name"
              :class="{ 'tracker-project-name--placeholder': !projectName }"
              data-testid="tracker-preview-name"
            >
              {{ projectName || 'Your project name' }}
            </div>
            <div class="tracker-stop">
              <div class="stop-button-circle">
                <span class="stop-square" />
              </div>
            </div>
            <div class="tracker-stats">
              <div class="stat-column">
                <div class="stat-label">Project: <span class="stat-value">{{ weeklyUsed }} / {{ weeklyTotal }}</span></div>
                <div class="stat-bar">
                  <div
                    class="stat-bar-fill"
                    :style="{ width: weeklyPct + '%' }"
                  />
                </div>
              </div>
              <div class="stat-column">
                <div class="stat-label">Weekly: <span class="stat-value">8:27 / 40:00</span></div>
                <div class="stat-bar">
                  <div class="stat-bar-fill stat-bar-fill--today" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <p class="preview-summary">
          Your team tracks time to this project.
          You can create as many projects as you'd like.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import AppInput from '~/components/AppInput.vue';
import AppSelect from '~/components/AppSelect.vue';
import AppSwitch from '~/components/AppSwitch.vue';
import { BUDGET_TYPE_HOURS } from '~/app_constants';
import { ClientsResource, ProjectsResource } from '~/resources';

export default {
  name: 'SetUpProject',
  components: {
    AppInput,
    AppSelect,
    AppSwitch,
  },
  inject: {
    wizardBus: { default: () => ({ on: () => {}, off: () => {} }) },
  },
  props: {
    project: {
      type: Object,
      default: null,
    },
    clients: {
      type: Array,
      default: () => [],
    },
    organization: {
      type: Object,
      required: true,
      validator: (value) => Object.keys(value).includes('id'),
    },
    hideBudgetCard: {
      type: Boolean,
      default: false,
    },
    currentUser: {
      type: Object,
      required: true,
      validator: (value) => Object.keys(value).includes('id'),
    },
  },
  data() {
    const budget = this.project && this.project.budget;

    return {
      projectName: this.project ? this.project.name : '',
      selectedClientId: this.project ? (this.project.client_id || '') : '',
      newClientName: '',
      budgetEnabled: !!budget,
      budgetAmount: budget ? String(budget.hours || 40) : '40',
      errors: { projectName: '', budgetAmount: '' },
    };
  },
  computed: {
    clientOptions() {
      return [
        { id: '', text: 'No client' },
        { id: 'new', text: '+ Add new client…' },
        ...this.clients.map((c) => ({ id: c.id, text: c.name })),
      ];
    },
    weeklyTotalHours() {
      if (this.budgetEnabled) {
        const parsed = parseFloat(this.budgetAmount);

        return Number.isFinite(parsed) && parsed > 0 ? parsed : 40;
      }

      return 40;
    },
    weeklyUsedHours() {
      return parseFloat((this.weeklyTotalHours * 0.263).toFixed(2));
    },
    weeklyUsed() {
      return this.formatHM(this.weeklyUsedHours);
    },
    weeklyTotal() {
      return this.formatHM(this.weeklyTotalHours);
    },
    weeklyPct() {
      return Math.min(100, Math.round((this.weeklyUsedHours / this.weeklyTotalHours) * 100));
    },
  },
  watch: {
    projectName() {
      this.emitCompletion();
    },
  },
  created() {
    this.wizardBus.on('userClickedContinue', this.handleFinishedSubstep);
    this.emitCompletion();
  },
  beforeUnmount() {
    this.wizardBus.off('userClickedContinue', this.handleFinishedSubstep);
  },
  methods: {
    emitCompletion() {
      this.$emit(this.projectName.trim() ? 'completeSubstep' : 'incompleteSubstep');
    },
    formatHM(hours) {
      const hh = Math.floor(hours);
      const mm = Math.round((hours - hh) * 60);

      return `${hh}:${String(mm).padStart(2, '0')}`;
    },
    validate() {
      this.errors = { projectName: '', budgetAmount: '' };
      let valid = true;

      if (!this.projectName.trim()) {
        this.errors.projectName = 'Project name is required.';
        valid = false;
      }

      if (this.budgetEnabled) {
        const amount = this.budgetAmount.trim();

        if (!amount) {
          this.errors.budgetAmount = 'Budget amount is required.';
          valid = false;
        } else if (!/^\d+$/.test(amount) || parseInt(amount, 10) <= 0) {
          this.errors.budgetAmount = 'Enter a whole number of hours greater than 0.';
          valid = false;
        }
      }

      return valid;
    },
    async handleFinishedSubstep() {
      if (!this.validate()) { return; }

      try {
        const clientId = await this.resolveClientId();
        const projectId = await this.saveProject(clientId);

        await this.saveBudget(projectId);
        this.$emit('finishSubstep');
      } catch (error) {
        if (![401, 403].includes(error.status)) {
          this.$appMessage('Something went wrong. Please try again.');
        }
      }
    },
    async saveBudget(projectId) {
      const budget = this.buildBudget();

      if (budget) {
        await ProjectsResource.updateBudget(
          { id: projectId },
          { project: { budget_attributes: budget } }
        );
      } else if (this.project && this.project.budget) {
        await ProjectsResource.removeBudget({ id: projectId });
      }
    },
    async resolveClientId() {
      if (this.selectedClientId === 'new' && this.newClientName.trim()) {
        const response = await ClientsResource.create(
          { id: this.organization.id },
          { client: { name: this.newClientName.trim() } }
        );

        return response.client.id;
      }

      return this.selectedClientId || null;
    },
    buildBudget() {
      if (!this.budgetEnabled || !this.budgetAmount) { return null; }

      const amount = parseFloat(this.budgetAmount);

      if (!Number.isFinite(amount) || amount <= 0) { return null; }

      return {
        type: BUDGET_TYPE_HOURS,
        rate: 0,
        hours: amount,
        recurrence: 'weekly',
      };
    },
    async saveProject(clientId) {
      if (this.project) {
        await ProjectsResource.update(
          { id: this.project.id },
          { project: { name: this.projectName, client_id: clientId } }
        );

        return this.project.id;
      }

      const response = await ProjectsResource.create(
        { id: this.organization.id },
        {
          skip_existing_project: true,
          projects: [
            {
              name: this.projectName,
              members: [{ user_id: this.currentUser.id, role: 'manager', selected: true }],
            },
          ],
        }
      );

      return response.projects[0].id;
    },
  },
};
</script>
